import { useQuery } from 'react-query'
import { Image, Package, TrendingUp } from 'lucide-react'
import { apiService as api } from '../services/api'

const ImageStats = () => {
  const { data: stats, isLoading } = useQuery(
    'image-stats',
    async () => {
      // Utiliser l'endpoint dédié aux statistiques d'images
      const response = await api.get('/rentman/image-stats')
      
      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.error || 'Erreur lors de la récupération des statistiques')
      }
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchInterval: 2 * 60 * 1000 // Refetch every 2 minutes
    }
  )

  if (isLoading) {
    return (
      <div className="card">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Image className="h-5 w-5 mr-2 text-orange-500" />
        Statistiques des images
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total produits */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total produits</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalProducts || 0}</p>
            </div>
            <Package className="h-8 w-8 text-gray-400" />
          </div>
        </div>

        {/* Produits avec images */}
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Avec images</p>
              <p className="text-2xl font-bold text-green-700">{stats?.productsWithImages || 0}</p>
            </div>
            <Image className="h-8 w-8 text-green-500" />
          </div>
        </div>

        {/* Pourcentage */}
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600">Couverture</p>
              <p className="text-2xl font-bold text-orange-700">{stats?.imagePercentage || 0}%</p>
            </div>
            <TrendingUp className="h-8 w-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Barre de progression */}
      <div className="mt-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progression des images</span>
          <span>{stats?.productsWithImages || 0} / {stats?.totalProducts || 0}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-orange-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${stats?.imagePercentage || 0}%` }}
          ></div>
        </div>
      </div>

      {/* Message d'encouragement */}
      {stats?.imagePercentage < 50 && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            💡 <strong>Astuce :</strong> Synchronisez plus de produits pour améliorer la couverture d'images.
          </p>
        </div>
      )}
      
      {stats?.imagePercentage >= 50 && stats?.imagePercentage < 90 && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            🎯 <strong>Bien !</strong> Vous avez une bonne couverture d'images. Continuez !
          </p>
        </div>
      )}
      
      {stats?.imagePercentage >= 90 && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            🎉 <strong>Excellent !</strong> Votre catalogue a une excellente couverture d'images !
          </p>
        </div>
      )}
    </div>
  )
}

export default ImageStats
